<template>
  <div class="portal-dashboard pa-4">
    <v-row v-if="loading">
      <v-col cols="12" class="text-center py-8">
        <v-progress-circular indeterminate color="red-darken-1" size="48" />
      </v-col>
    </v-row>

    <template v-else-if="dashboard">
      <!-- Salut angajat -->
      <div class="greeting-section mb-6">
        <h2 class="greeting-title">Bună ziua, <strong>{{ dashboard.NumeAngajat }}</strong>!</h2>
        <p class="greeting-sub text-medium-emphasis">{{ dashboard.Departament }} · {{ dashboard.Functie }}</p>
      </div>

      <!-- Cards KPI -->
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="3">
          <v-card class="kpi-card" color="green-lighten-5" elevation="2" rounded="xl">
            <v-card-text class="text-center py-5">
              <v-icon size="40" color="green-darken-2" class="mb-2">mdi-umbrella-beach</v-icon>
              <div class="kpi-value text-green-darken-2">{{ dashboard.ZileCODisponibile }}</div>
              <div class="kpi-label">Zile CO disponibile</div>
              <div class="kpi-sub text-medium-emphasis">Folosite: {{ dashboard.ZileCOFolosite }}</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="kpi-card" :color="dashboard.CereriConcediuInAsteptare > 0 ? 'amber-lighten-5' : 'grey-lighten-5'" elevation="2" rounded="xl">
            <v-card-text class="text-center py-5">
              <v-icon size="40" :color="dashboard.CereriConcediuInAsteptare > 0 ? 'amber-darken-2' : 'grey'" class="mb-2">mdi-clock-outline</v-icon>
              <div class="kpi-value" :class="dashboard.CereriConcediuInAsteptare > 0 ? 'text-amber-darken-2' : 'text-grey'">{{ dashboard.CereriConcediuInAsteptare }}</div>
              <div class="kpi-label">Cereri în așteptare</div>
              <div class="kpi-sub text-medium-emphasis">Concedii</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="kpi-card" :color="dashboard.TicheteInAsteptare > 0 ? 'blue-lighten-5' : 'grey-lighten-5'" elevation="2" rounded="xl">
            <v-card-text class="text-center py-5">
              <v-icon size="40" :color="dashboard.TicheteInAsteptare > 0 ? 'blue-darken-2' : 'grey'" class="mb-2">mdi-ticket-outline</v-icon>
              <div class="kpi-value" :class="dashboard.TicheteInAsteptare > 0 ? 'text-blue-darken-2' : 'text-grey'">{{ dashboard.TicheteInAsteptare }}</div>
              <div class="kpi-label">Tichete deschise</div>
              <div class="kpi-sub text-medium-emphasis">HR</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="kpi-card" :color="dashboard.PontajPlecareLipsaAzi ? 'red-lighten-5' : 'green-lighten-5'" elevation="2" rounded="xl">
            <v-card-text class="text-center py-5">
              <v-icon size="40" :color="dashboard.PontajPlecareLipsaAzi ? 'red-darken-2' : 'green-darken-2'" class="mb-2">
                {{ dashboard.PontajPlecareLipsaAzi ? 'mdi-door-open' : 'mdi-check-circle' }}
              </v-icon>
              <div class="kpi-value" :class="dashboard.PontajPlecareLipsaAzi ? 'text-red-darken-2' : 'text-green-darken-2'">
                {{ dashboard.PontajPlecareLipsaAzi ? '!' : 'OK' }}
              </div>
              <div class="kpi-label">Pontaj azi</div>
              <div class="kpi-sub text-medium-emphasis">
                {{ dashboard.PontajPlecareLipsaAzi ? 'Plecare lipsă' : 'Complet' }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Acțiuni rapide -->
      <v-row>
        <v-col cols="12" sm="6" md="3" v-for="act in actiuniRapide" :key="act.route">
          <v-card class="action-card" elevation="1" rounded="xl" @click="$router.push({ name: act.route })" :ripple="true" style="cursor:pointer">
            <v-card-text class="d-flex align-center pa-4">
              <v-avatar :color="act.color" size="44" class="mr-3">
                <v-icon color="white" size="22">{{ act.icon }}</v-icon>
              </v-avatar>
              <div>
                <div class="action-title">{{ act.title }}</div>
                <div class="action-sub text-medium-emphasis text-caption">{{ act.sub }}</div>
              </div>
              <v-spacer />
              <v-icon color="grey-lighten-1">mdi-chevron-right</v-icon>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-alert v-else-if="error" type="error" variant="tonal">{{ error }}</v-alert>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import PORTAL_Dashboard from '@/services/Portal/PORTAL_Dashboard'

export default defineComponent({
  name: 'PortalDashboard',
  data() {
    return {
      loading: false,
      error: '',
      dashboard: null as any,
      actiuniRapide: [
        { title: 'Cereri concediu', sub: 'Depune sau urmărește', icon: 'mdi-umbrella-beach', color: 'green-darken-1', route: 'PortalCereriConcediu' },
        { title: 'Tichete HR', sub: 'Adeverințe, cereri', icon: 'mdi-ticket', color: 'blue-darken-1', route: 'PortalTichete' },
        { title: 'Pontaj', sub: 'Vizualizare lunar', icon: 'mdi-clock', color: 'purple-darken-1', route: 'PortalPontaj' },
        { title: 'Profil', sub: 'Date personale', icon: 'mdi-account', color: 'orange-darken-1', route: 'PortalProfil' },
      ]
    }
  },
  async mounted() {
    this.loading = true;
    try {
      const resp = await PORTAL_Dashboard.get();
      this.dashboard = resp.data;
    } catch (e: any) {
      this.error = 'Nu s-au putut încărca datele dashboard-ului.';
    } finally {
      this.loading = false;
    }
  }
})
</script>

<style scoped>
.greeting-title { font-size: 1.5rem; font-weight: 600; color: #1a202c; }
.greeting-sub { font-size: 0.9rem; }
.kpi-value { font-size: 2.5rem; font-weight: 700; line-height: 1; margin-bottom: 0.25rem; }
.kpi-label { font-size: 0.875rem; font-weight: 600; color: #333; }
.kpi-sub { font-size: 0.75rem; margin-top: 0.25rem; }
.action-title { font-size: 0.9rem; font-weight: 600; color: #1a202c; }
.action-card { transition: all 0.2s; }
.action-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.1) !important; }
</style>
